import styled from 'styled-components';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExpandingContainer } from 'design-system/src/components/ExpandingContainer/ExpandingContainer';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { Typography } from 'design-system/src/components/Typography/Typography';
import ChevronDown from 'design-system/src/assets/icons/chevronDown.svg?react';
import { standardTransitionStyles, focusVisibleStyles, focusVisibleInsetStyles } from 'design-system/src/sharedStyles';
import { Link } from 'react-router-dom';
import { client } from '../../../api';
import { formatSimpleDate } from '../../../utils';

const StyledPendingEnrollmentForms = styled.div`
  border: 1px solid var(--border-gray);
  border-radius: 8px;
  margin-top: 16px;
`;

const PendingEnrollmentFormTrigger = styled.button<{ $isOpen: boolean }>`
  all: unset;
  // border-radius is set on this element and parent to avoid clipping
  border-radius: 8px;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 8px 12px;
  background-color: var(--light-surface);
  cursor: pointer;
  border-bottom: 1px solid var(--border-gray);
  border-color: ${({ $isOpen }) => ($isOpen ? 'var(--border-gray)' : 'transparent')};
  // specify the corner radii instead of using overflow: hidden to avoid cutting off the focus outline
  border-bottom-left-radius: ${({ $isOpen }) => ($isOpen ? '0' : 'var(--border-radius-medium)')};
  border-bottom-right-radius: ${({ $isOpen }) => ($isOpen ? '0' : 'var(--border-radius-medium)')};

  ${standardTransitionStyles}
  ${focusVisibleStyles}

  svg {
    transform: rotate(${({ $isOpen }) => ($isOpen ? '180deg' : '0deg')});
    transition: transform 0.2s ease-out;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }

  &:not(:disabled):hover {
    background-color: var(--surface);
  }

  &:disabled {
    cursor: auto;
  }
`;

const TriggerContents = styled(Typography)`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const StyledLoadingSpinner = styled(LoadingSpinner)`
  margin-left: 2px;

  // make the spinner the same size as the list number that replaces it
  svg {
    width: 16px;
    height: 16px;
  }
`;

const EnrollmentList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const EnrollmentItem = styled(Typography)`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 12px;
  &:not(:last-child) {
    border-bottom: 1px solid var(--light-border-gray);
  }
  ${standardTransitionStyles}

  &:hover {
    background-color: var(--light-border-gray);
    cursor: pointer;
  }

  ${focusVisibleInsetStyles}
`;

type PendingEnrollment = {
  id: string;
  created_at: string;
  patient_first_name: string;
  patient_last_name: string;
  drug_name: string;
};

/**
 * List of pending enrollment forms that haven't been matched to a prescription
 */
export const PendingEnrollmentForms = () => {
  const [showList, setShowList] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['pending-enrollment-forms'],
    queryFn: async () => {
      const res = await client.get('/enrollments/pending');
      return res.data;
    },
  });

  const pendingEnrollments = data?.pending_enrollments ?? [];
  const disabled = pendingEnrollments.length === 0;

  return (
    <StyledPendingEnrollmentForms>
      <PendingEnrollmentFormTrigger $isOpen={showList} onClick={() => setShowList(!showList)} disabled={disabled}>
        <TriggerContents styledAs="bodySmallDMSans" weight="600" renderedAs="div">
          Pending enrollment forms
          {isLoading ? <StyledLoadingSpinner height="16px" /> : <div>({pendingEnrollments.length})</div>}
        </TriggerContents>
        {!disabled && <ChevronDown title={showList ? 'Hide list' : 'Show list'} />}
      </PendingEnrollmentFormTrigger>
      <ExpandingContainer visible={showList}>
        <EnrollmentList>
          {pendingEnrollments.map((enrollment: PendingEnrollment) => (
            <li key={enrollment.id}>
              <EnrollmentItem styledAs="bodySmallDMSans" to={`/enrollments/pending/${enrollment.id}`} renderedAs={Link}>
                {formatSimpleDate(enrollment.created_at)} ·<strong>{enrollment.drug_name}</strong> for
                <strong>
                  {enrollment.patient_first_name} {enrollment.patient_last_name}
                </strong>
              </EnrollmentItem>
            </li>
          ))}
        </EnrollmentList>
      </ExpandingContainer>
    </StyledPendingEnrollmentForms>
  );
};

export default PendingEnrollmentForms;
